import {useState, useEffect, useRef} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { Scrollbar } from 'react-scrollbars-custom';
import useBodyClass from '../../common/hooks/useBodyClassName';
import InputGroup from '../../common/InputGroup';
import DateMonthPicker from '../../common/DatepickerPortal';
import PrintPdfDropdown from '../../common/export_documents/PrintPdfDropdown';
import { newSelectStyles } from '../../common/shared/select.styled';
import { constants } from '../../common/constants/constants';
import { CurrencyValue } from '../../common/currencyValue';
import { useAccessToken } from '../../common/hooks/useAccessToken';
import { showLoader, hideLoader } from '../../store/common/slice';
import Search from '../../images/search.svg';
import bank from '../../images/newDesign/paymentMethods/bank.svg';
import creditCard from '../../images/newDesign/paymentMethods/creditCard.svg';
import { StyledReport } from '../reports/report.styled';
import { StyledCollectionStatus } from './CollectionStatus.styled';
import BankDeposit from './BankDeposit';
import BllinkCharges from './BllinkCharges';
import {useDebounce} from "../../common/hooks/useDebounce";
import { PageTitle } from '../../common/PageTitle';


const CollectionStatus = ({ match, address }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { buildingID } = match.params;
    const client = useAccessToken();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const pdfContainer = useRef();
    const titleData = useSelector(state => state.buildings.titleData);

    useBodyClass('report');

    const [currentTab, setCurrentTab] = useState('bankDeposit');
    const [filter, setFilter] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [totalAmount, setTotalAmount] = useState(0);
    const [currency, setCurrency] = useState(constants.defaultCurrency);
    const [statusesOptions, setStatusesOptions] = useState([]);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [depositRecords, setDepositRecords] = useState([]);
    const [filteredDepositRecords, setFilteredDepositRecords] = useState([]);
    const [bllinkRecords, setBllinkRecords] = useState([]);
    const [filteredBllinkRecords, setFilteredBllinkRecords] = useState([]);
    const [parsedRecords, setParsedRecords] = useState([]);
    const [currentBankOption, setCurrentBankOption] = useState('deposit');
    const [currentBllinkOption, setCurrentBllinkOption] = useState('charges');
    const [titleRow, setTitleRow] = useState([{}]);
    const [pdfDepositContainer, setPdfDepositContainer] = useState(false);
    const [pdfChargesContainer, setPdfChargesContainer] = useState(false);

    const deferredFilter = useDebounce(filter, 600);

    const pdfContainerActivate = () => {
        if (currentTab === 'bankDeposit') {
            setPdfDepositContainer(true);
            setPdfChargesContainer(false);
        } else if (currentTab === 'bllinkCharges') {
            setPdfChargesContainer(true);
            setPdfDepositContainer(false);
        }
    }

    const titleDepositRow = [{
        depositDate: t('expenses.deposit_date'),
        amount: t('reports.amount'),
        apartmentNum: t('reports.apt_num'),
        tenantName: t('reports.tenantName'),
        paidFor: t('reports.paid_for'),
        type: t('reports.payment_type'),
        status: t('expenses.status'),
    }];

    const titleChargesRow = [{
        chargeDate: t('reports.charge_date_title'),
        depositDate: t('expenses.deposit_date'),
        type: t('reports.payment_type'),
        apartmentNum: t('reports.apt_num'),
        tenantName: t('reports.tenantName'),
        amount: t('reports.amount'),
        paidFor: t('reports.paid_for'),
        status: t('expenses.status'),
    }];

    const csvCallback = item => setParsedRecords(prev => prev.concat([item]));

    const changeTab = type => {
        setCurrentTab(type);
        setParsedRecords([]);
        if (currentTab === 'bankDeposit') setTitleRow(titleDepositRow)
        else if (currentTab === 'bllinkCharges') setTitleRow(titleChargesRow)
    };

    useEffect(() => {
        let statuses = [
            { label: t('reports.all_statuses'), value: 'any' },
            // not sure if we need to change value here for deposit or leave as it is
            { label: t('statuses.depositCollectionReport'), value: 'success' },
            { label: t('statuses.pendingCollectionReport'), value: 'pending' },
        ];
        if (currentTab === 'bllinkCharges') {
            const keysArr = ['1', '2', '5', '6', '7', '8', '9', '10', '11', '12', '13'];
            statuses = [
                { label: t('reports.all_statuses'), value: 'all' }
            ];
            for (const value of keysArr) {
                const key = constants.PaymentPlanDetailsStatus[value];
                statuses.push({ label: t(`statuses.${key}`), value });
            }
        }
        setStatusesOptions(statuses);
        setCurrentStatus(statuses[0]);
        setParsedRecords([]);
        setTitleRow(titleDepositRow);
    }, []);

    const handlePaymentStop = async () => {
        return loadRecords(startDate, endDate);
    };

    const components = {
        bankDeposit: {
            component: BankDeposit,
            props: {
                records: filteredDepositRecords,
                container: pdfContainer,
                client,
                buildingID,
                csvCallback,
                pdfDepositContainer,
                address
            }
        },
        bllinkCharges: {
            component: BllinkCharges,
            props: {
                records: filteredBllinkRecords,
                container: pdfContainer,
                csvCallback,
                buildingID,
                client,
                onPaymentStop: handlePaymentStop,
                pdfChargesContainer,
                address
            }
        },
    };
    const tabComponent = components[currentTab];

    const loadRecords = async (start, end, status, search) => {
        dispatch(showLoader());

        const dateRange = {
            from: { year: start.getFullYear(), month: start.getMonth() + 1 },
            to: { year: end.getFullYear(), month: end.getMonth() + 1 }
        };

        let response;
        if (currentTab === 'bankDeposit') {
            response = await client.managers.reports.getCollectionReportDeposits(buildingID, dateRange, status, search);
            response.sort((a,b) => {
                return new Date(a.depositDate) - new Date(b.depositDate);
            });
        } else if (currentTab === 'bllinkCharges') {
            const { rows } = await client.managers.reports.getCollectionReportCharges(buildingID, dateRange, search);
            response = rows;
            response.sort((a,b) => {
                return new Date(b.depositDate) - new Date(a.depositDate);
            });
        }

        if (response) {
            if (currentTab === 'bankDeposit') {
                setDepositRecords(response);
                setFilteredDepositRecords(response);
            } else if (currentTab === 'bllinkCharges') {
                setBllinkRecords(response);
                setFilteredBllinkRecords(response);
            }

            let init = 0;
            const sum = response.reduce((prev, curr) => prev + curr.amount.value, init);
            setTotalAmount(sum);
            setCurrency(response[0]?.amount.currency);
        }
        dispatch(hideLoader());
    };

    useEffect(() => {
        if (client != null && currentStatus != null) loadRecords(startDate, endDate, currentStatus.value, filter);
    }, [client, currentTab, startDate, endDate, currentStatus, deferredFilter]);

    useEffect(() => {
        setFilteredDepositRecords(depositRecords);
        setFilteredBllinkRecords(bllinkRecords);
        const sum = (currentTab === 'bankDeposit' ? depositRecords : bllinkRecords).reduce((prev, curr) => prev + curr.amount.value, 0);
        setTotalAmount(sum);
    }, [depositRecords, bllinkRecords]);

    const onStartDateChange = date => setStartDate(date);

    const onEndDateChange = date => setEndDate(date);

    return (
        <StyledCollectionStatus ltr={ltr}>
            <PageTitle text={t('reports.collection_report')}>
                <PrintPdfDropdown
                    className='report-pdf-icons'
                    buildingID={buildingID}
                    eventLabel="collectionReport"
                    titleString={t('reports.collection_report')}
                    excelTitle={t('reports.collection_report')}
                    reportName={t('reports.collection_report')}
                    pdfContainer={pdfContainer.current}
                    rows={parsedRecords}
                    titleRow={titleRow}
                    onClick={pdfContainerActivate}
                />
            </PageTitle>

            <div className='switch-btns-wrapper'>
                <div className={`switch-btn ${currentTab === 'bankDeposit' && 'active'}`} onClick={() => changeTab('bankDeposit')}>
                    <img src={bank} />
                    {t('reports.bank_deposit')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>

                <div className={`switch-btn ${currentTab === 'bllinkCharges' && 'active'}`} onClick={() => changeTab('bllinkCharges')}>
                    <img src={creditCard} />
                    {t('reports.bllink_charges')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
            </div>

            <div className='main-wrapper'>
                <Scrollbar
                    style={{ width: '100%', height: 'calc(100vh - 250px)' }}
                    trackYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-track" />;
                        }
                    }}
                    thumbYProps={{
                        renderer: props => {
                            const { elementRef, ...restProps } = props;
                            return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                        }
                    }}
                    rtl={false}
                    noScrollX
                >
                    <StyledReport className='report' ltr={ltr}>
                        <div className='general-headers'>
                            <div></div>
                            <div>{t('offline_payments.date')}</div>
                            <div>{t('expenses.status')}</div>
                        </div>
                        <div className='report-wrap'>
                            <div className='report-top'>
                                <div className='report-top-start'>
                                    <div className="search">
                                        <input
                                            type="text"
                                            name="search"
                                            onChange={e => setFilter(e.target.value.toLowerCase())}
                                            value={filter}
                                            placeholder={t('reports.fast_search')}
                                        />
                                        <img src={Search} alt={t('search.search')} />
                                    </div>
                                </div>

                                <div className='datepickers-container'>
                                    <InputGroup
                                        labelText={`${t('expenses.from')}`}
                                        labelFor='from'
                                        labelClassName='datepicker-label'
                                    >
                                        <DateMonthPicker
                                            date={startDate}
                                            onChange={onStartDateChange}
                                            id='from'
                                            maxDate={endDate}
                                            language={i18n.language}
                                            className='one-line-design month-year-calendar'
                                            showMonthYear
                                            withPortal={false}
                                        />
                                    </InputGroup>

                                    <InputGroup
                                        labelText={`${t('expenses.to')}`}
                                        labelFor='to'
                                        labelClassName='datepicker-label'
                                    >
                                        <DateMonthPicker
                                            date={endDate}
                                            onChange={onEndDateChange}
                                            id='to'
                                            minDate={startDate}
                                            language={i18n.language}
                                            className='one-line-design month-year-calendar'
                                            showMonthYear
                                            withPortal={false}
                                        />
                                    </InputGroup>
                                </div>

                                <Select
                                    className='select'
                                    styles={newSelectStyles}
                                    options={statusesOptions}
                                    value={currentStatus}
                                    onChange={e => setCurrentStatus(e)}
                                    isRtl={!ltr}
                                />
                            </div>
                        </div>

                        {currentTab === 'bankDeposit' && (
                            <div className='switch-btns-wrapper bank-deposit-switches'>
                                <div className={`switch-btn ${currentBankOption === 'deposit' && 'active'}`} onClick={() => setCurrentBankOption('deposit')}>
                                    {t('reports.deposit')}
                                    <div className='radio-btn-outer'>
                                        <div className='radio-btn-inner'></div>
                                    </div>
                                </div>
    
                                <div className={`switch-btn disabled ${currentBankOption === 'deductions' && 'active'}`} onClick={() => setCurrentBankOption('deductions')}>
                                    {t('reports.deductions')}
                                    <div className='radio-btn-outer'>
                                        <div className='radio-btn-inner'></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentTab === 'bllinkCharges' && (
                            <div className='switch-btns-wrapper bank-deposit-switches'>
                                <div className={`switch-btn ${currentBllinkOption === 'charges' && 'active'}`} onClick={() => setCurrentBllinkOption('charges')}>
                                    {t('reports.charges')}
                                    <div className='radio-btn-outer'>
                                        <div className='radio-btn-inner'></div>
                                    </div>
                                </div>

                                <div className={`switch-btn disabled ${currentBllinkOption === 'refund' && 'active'}`} onClick={() => setCurrentBllinkOption('refund')}>
                                    {t('reports.collection_refund')}
                                    <div className='radio-btn-outer'>
                                        <div className='radio-btn-inner'></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='total-amount'>
                            <span className='total'>{t('reports.totalOffsets')}:</span>
                            <CurrencyValue currency={currency}>{totalAmount}</CurrencyValue>
                        </div>
                    </StyledReport>

                    <tabComponent.component
                        {...tabComponent.props}
                    />
                </Scrollbar>
            </div>
        </StyledCollectionStatus>
    )
}

export default CollectionStatus;
